"use client";

import { captureClarityEvent } from "@/lib/analytics/clarity";

type AnalyticsParamValue = string | number | boolean | undefined;
type AnalyticsParams = Record<string, AnalyticsParamValue>;

const GA4_MAX_EVENT_NAME_LENGTH = 40;
const GA4_MAX_PARAM_NAME_LENGTH = 40;

interface MirrorOptions {
  ga4?: boolean;
  clarity?: boolean;
}

function normalizeGa4Name(name: string, maxLength: number): string {
  const normalized = name
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  const withLetterPrefix = /^[A-Za-z]/.test(normalized)
    ? normalized
    : `event_${normalized}`;
  return withLetterPrefix.slice(0, maxLength);
}

export function toGa4EventName(name: string): string {
  return normalizeGa4Name(name, GA4_MAX_EVENT_NAME_LENGTH);
}

function sanitizeGa4Params(params?: object): AnalyticsParams | undefined {
  if (!params) return undefined;
  const out: AnalyticsParams = {};
  for (const [rawKey, value] of Object.entries(params)) {
    if (
      typeof value !== "string" &&
      typeof value !== "number" &&
      typeof value !== "boolean"
    ) {
      continue;
    }
    const key = normalizeGa4Name(rawKey, GA4_MAX_PARAM_NAME_LENGTH);
    if (!key) continue;
    out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function captureGa4Event(name: string, params?: object): void {
  if (typeof window === "undefined") return;
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (!gtag) return;
  const eventName = toGa4EventName(name);
  if (!eventName) return;
  gtag("event", eventName, sanitizeGa4Params(params));
}

export function mirrorAnalyticsEvent(
  name: string,
  params?: object,
  options?: MirrorOptions,
): void {
  if (options?.ga4 !== false) captureGa4Event(name, params);
  if (options?.clarity !== false) captureClarityEvent(name);
}
